import BackendService from 'BackendService';
import React, { useCallback } from 'react';
import type { SimplifiedFundUpdateEditorHandle } from './SimplifiedFundUpdateEditor.types';
import type { AdminFundUpdateEntry, UpdateCard } from '../../models/FundUpdateEntry';
import type {
  FundUpdatesAdminState,
  HighlightEditState,
  HighlightSuggestion,
} from './fundUpdatesAdmin.types';
import { inferHighlightFromContent } from './fundUpdatesAdminUtils';

type SetFundUpdatesState = React.Dispatch<React.SetStateAction<FundUpdatesAdminState>>;

export function useFundUpdateHighlights(
  fundOpportunityId: string,
  setState: SetFundUpdatesState,
  getCmsRef: (id: string) => React.RefObject<SimplifiedFundUpdateEditorHandle>,
  state: Pick<
    FundUpdatesAdminState,
    'highlightEdits' | 'highlightEditingId' | 'highlightSuggestion' | 'generateConfirm'
  >,
) {
  const toggleHighlightSection = useCallback(
    (id: string) => {
      setState((prev) => {
        const highlightOpenIds = new Set(prev.highlightOpenIds);
        if (highlightOpenIds.has(id)) highlightOpenIds.delete(id);
        else highlightOpenIds.add(id);
        return { ...prev, highlightOpenIds };
      });
    },
    [setState],
  );

  const startHighlightEdit = useCallback(
    (update: AdminFundUpdateEntry) => {
      const card = update.updateCard;
      const inferred = inferHighlightFromContent(update.content ?? []);
      const editState: HighlightEditState = {
        title: card?.properties.title ?? inferred.title,
        summary: card?.properties.summary ?? inferred.summary,
        imageUrl: card?.properties.imageUrl ?? inferred.imageUrl ?? '',
      };
      setState((prev) => ({
        ...prev,
        highlightEditingId: update.externalId,
        highlightSuggestion:
          prev.highlightSuggestion?.id === update.externalId ? null : prev.highlightSuggestion,
        highlightEdits: { ...prev.highlightEdits, [update.externalId]: editState },
        highlightOpenIds: new Set([...prev.highlightOpenIds, update.externalId]),
      }));
    },
    [setState],
  );

  const cancelHighlightEdit = useCallback(() => {
    setState((prev) => ({ ...prev, highlightEditingId: null }));
  }, [setState]);

  const handleHighlightEditChange = useCallback(
    (id: string, field: keyof HighlightEditState, value: string) => {
      setState((prev) => ({
        ...prev,
        highlightEdits: {
          ...prev.highlightEdits,
          [id]: { ...prev.highlightEdits[id], [field]: value },
        },
      }));
    },
    [setState],
  );

  const handleSaveHighlight = useCallback(
    (update: AdminFundUpdateEntry) => {
      const id = update.externalId;
      const edit = state.highlightEdits[id];
      if (!edit) return;
      const card: UpdateCard = {
        type: 'InvestorProgressUpdateCard',
        properties: {
          title: edit.title.trim(),
          summary: edit.summary.trim(),
          imageUrl: edit.imageUrl || null,
        },
      };
      getCmsRef(id).current?.setUpdateCard(card);
      getCmsRef(id).current?.enterEditMode();
      setState((prev) => ({
        ...prev,
        highlightEditingId: null,
        drafts: prev.drafts.map((d) => (d.externalId === id ? { ...d, updateCard: card } : d)),
        published: prev.published.map((p) =>
          p.externalId === id ? { ...p, updateCard: card } : p,
        ),
      }));
    },
    [getCmsRef, setState, state.highlightEdits],
  );

  const handleClearHighlight = useCallback(
    (update: AdminFundUpdateEntry) => {
      const id = update.externalId;
      getCmsRef(id).current?.setUpdateCard(null);
      getCmsRef(id).current?.enterEditMode();
      setState((prev) => ({
        ...prev,
        drafts: prev.drafts.map((d) => (d.externalId === id ? { ...d, updateCard: null } : d)),
        published: prev.published.map((p) =>
          p.externalId === id ? { ...p, updateCard: null } : p,
        ),
      }));
    },
    [getCmsRef, setState],
  );

  const doGenerateHighlight = useCallback(
    async (update: AdminFundUpdateEntry, useProModel = false, refinementPrompt?: string) => {
      const id = update.externalId;
      const { highlightEdits, highlightEditingId } = state;

      type HighlightCardSnapshot =
        | { title: string; summary: string; imageUrl?: string | null }
        | undefined;

      const editState = highlightEdits[id];
      const currentCard: HighlightCardSnapshot =
        highlightEditingId === id && editState
          ? {
              title: editState.title,
              summary: editState.summary,
              imageUrl: editState.imageUrl || null,
            }
          : update.updateCard
            ? {
                title: update.updateCard.properties.title,
                summary: update.updateCard.properties.summary,
                imageUrl: update.updateCard.properties.imageUrl ?? null,
              }
            : undefined;

      setState((prev) => {
        const highlightGeneratingIds = new Set(prev.highlightGeneratingIds);
        highlightGeneratingIds.add(id);
        const highlightGeneratingProIds = new Set(prev.highlightGeneratingProIds);
        if (useProModel) highlightGeneratingProIds.add(id);
        else highlightGeneratingProIds.delete(id);
        return {
          ...prev,
          highlightGeneratingIds,
          highlightGeneratingProIds,
          highlightSuggestion: null,
          highlightOpenIds: new Set([...prev.highlightOpenIds, id]),
        };
      });
      getCmsRef(id).current?.setPublishBlocked(true);
      try {
        const result = await (
          new BackendService() as BackendService & {
            generateFundUpdateHighlight: (
              fundId: string,
              updateId: string,
              refinementPrompt: string | undefined,
              useProModel: boolean,
              currentCard: HighlightCardSnapshot,
            ) => Promise<Omit<HighlightSuggestion, 'id'>>;
          }
        ).generateFundUpdateHighlight(
          fundOpportunityId,
          id,
          refinementPrompt || undefined,
          useProModel,
          currentCard,
        );
        setState((prev) => {
          const highlightGeneratingIds = new Set(prev.highlightGeneratingIds);
          highlightGeneratingIds.delete(id);
          const highlightGeneratingProIds = new Set(prev.highlightGeneratingProIds);
          highlightGeneratingProIds.delete(id);
          return {
            ...prev,
            highlightGeneratingIds,
            highlightGeneratingProIds,
            highlightSuggestion: { id, ...result },
          };
        });
      } catch (err) {
        console.error('[FundUpdatesAdminPanel] generateHighlight failed:', err);
        setState((prev) => {
          const highlightGeneratingIds = new Set(prev.highlightGeneratingIds);
          highlightGeneratingIds.delete(id);
          const highlightGeneratingProIds = new Set(prev.highlightGeneratingProIds);
          highlightGeneratingProIds.delete(id);
          return {
            ...prev,
            highlightGeneratingIds,
            highlightGeneratingProIds,
            error: 'Unable to generate highlight card. Please try again.',
          };
        });
        getCmsRef(id).current?.setPublishBlocked(false);
      }
    },
    [fundOpportunityId, getCmsRef, setState, state],
  );

  const handleGenerateHighlight = useCallback(
    async (update: AdminFundUpdateEntry, useProModel = false, refinementPrompt?: string) => {
      const id = update.externalId;
      const cmsRef = getCmsRef(id);
      if (cmsRef.current?.state.requireSave) {
        setState((prev) => ({
          ...prev,
          generateConfirm: { update, useProModel, refinementPrompt },
        }));
        return;
      }
      await doGenerateHighlight(update, useProModel, refinementPrompt);
    },
    [doGenerateHighlight, getCmsRef, setState],
  );

  const handleConfirmSaveAndGenerate = useCallback(async () => {
    const pending = state.generateConfirm;
    if (!pending) return;

    const { update, useProModel, refinementPrompt } = pending;
    const id = update.externalId;

    setState((prev) => {
      const saveForGenerateIds = new Set(prev.saveForGenerateIds);
      saveForGenerateIds.add(id);
      const saveForGenerateProIds = new Set(prev.saveForGenerateProIds);
      if (useProModel) saveForGenerateProIds.add(id);
      else saveForGenerateProIds.delete(id);
      return { ...prev, generateConfirm: null, saveForGenerateIds, saveForGenerateProIds };
    });

    const cmsRef = getCmsRef(id);
    try {
      await cmsRef.current?.saveContent();
    } catch (err) {
      console.error('[FundUpdatesAdminPanel] save before generate failed:', err);
      setState((prev) => {
        const saveForGenerateIds = new Set(prev.saveForGenerateIds);
        saveForGenerateIds.delete(id);
        const saveForGenerateProIds = new Set(prev.saveForGenerateProIds);
        saveForGenerateProIds.delete(id);
        return {
          ...prev,
          saveForGenerateIds,
          saveForGenerateProIds,
          error: 'Unable to save content before generating. Please try again.',
        };
      });
      return;
    }
    setState((prev) => {
      const saveForGenerateIds = new Set(prev.saveForGenerateIds);
      saveForGenerateIds.delete(id);
      const saveForGenerateProIds = new Set(prev.saveForGenerateProIds);
      saveForGenerateProIds.delete(id);
      return { ...prev, saveForGenerateIds, saveForGenerateProIds };
    });
    await doGenerateHighlight(update, useProModel, refinementPrompt);
  }, [doGenerateHighlight, getCmsRef, setState, state.generateConfirm]);

  const handleAcceptSuggestion = useCallback(
    (update: AdminFundUpdateEntry) => {
      const { highlightSuggestion } = state;
      if (!highlightSuggestion || highlightSuggestion.id !== update.externalId) return;
      const id = update.externalId;
      const card: UpdateCard = {
        type: 'InvestorProgressUpdateCard',
        properties: {
          title: highlightSuggestion.title.trim(),
          summary: highlightSuggestion.summary.trim(),
          imageUrl: highlightSuggestion.imageUrl || null,
        },
      };
      getCmsRef(id).current?.setUpdateCard(card);
      getCmsRef(id).current?.enterEditMode();
      getCmsRef(id).current?.setPublishBlocked(false);
      setState((prev) => ({
        ...prev,
        highlightSuggestion: null,
        drafts: prev.drafts.map((d) => (d.externalId === id ? { ...d, updateCard: card } : d)),
        published: prev.published.map((p) =>
          p.externalId === id ? { ...p, updateCard: card } : p,
        ),
      }));
    },
    [getCmsRef, setState, state.highlightSuggestion],
  );

  const handleRejectSuggestion = useCallback(() => {
    const id = state.highlightSuggestion?.id;
    if (id) getCmsRef(id).current?.setPublishBlocked(false);
    setState((prev) => ({ ...prev, highlightSuggestion: null }));
  }, [getCmsRef, setState, state.highlightSuggestion?.id]);

  const handleRefinementPromptChange = useCallback(
    (id: string, value: string) => {
      setState((prev) => ({
        ...prev,
        highlightRefinementPrompts: { ...prev.highlightRefinementPrompts, [id]: value },
      }));
    },
    [setState],
  );

  const handleSuggestionImageSelect = useCallback(
    (update: AdminFundUpdateEntry, imageUrl: string) => {
      const suggestion = state.highlightSuggestion;
      if (!suggestion || suggestion.id !== update.externalId) return;
      const editState: HighlightEditState = {
        title: suggestion.title,
        summary: suggestion.summary,
        imageUrl,
      };
      setState((prev) => ({
        ...prev,
        highlightSuggestion: null,
        highlightEditingId: update.externalId,
        highlightEdits: { ...prev.highlightEdits, [update.externalId]: editState },
        highlightOpenIds: new Set([...prev.highlightOpenIds, update.externalId]),
      }));
    },
    [setState, state.highlightSuggestion],
  );

  return {
    toggleHighlightSection,
    startHighlightEdit,
    cancelHighlightEdit,
    handleHighlightEditChange,
    handleSaveHighlight,
    handleClearHighlight,
    handleGenerateHighlight,
    handleConfirmSaveAndGenerate,
    handleAcceptSuggestion,
    handleRejectSuggestion,
    handleRefinementPromptChange,
    handleSuggestionImageSelect,
  };
}
